import { Injectable } from '@angular/core';
import { isNotNullish } from '@fmnts/core';
import {
  AbstractGraphNodeBuilder,
  DeclarativeFieldComponent,
  FieldDefinition,
  GraphBuilderContext,
  GraphInputDefinition,
  isFieldDeclaration,
  isFormSelectField,
  OperatorNodeDefinition,
  SelectInputItem,
} from '@fmnts/declarative-forms';
import { filter, of, OperatorFunction } from 'rxjs';
import { filterValueInSelectItems } from '../rxjs/operators/filter-value-in-select-items.operator';
import { mapToSelectedItem } from '../rxjs/operators/map-to-select-item.operator';

export interface FieldReference {
  /**
   * Can be used to reference a field by its `key`.
   */
  fieldKey?: string;
  /**
   * You can also pass an string tuple to reference a field by
   * its key inside a fieldset, like so `['fieldset-key', 'field-key']`
   */
  path?: [string, string];
  /**
   * If `true`, the field for which the source/operator definition is evaluated
   * will be used as a reference
   */
  self?: boolean;
}

export interface FormFieldProperty {
  property: keyof FieldDefinition;
  isWidgetProperty?: boolean;
  ref?: FieldReference;
}

export type FormFieldSource = GraphInputDefinition<FieldReference>;

export enum FieldNodeName {
  FieldProperty = 'field.property',
}

export enum FieldOperatorName {
  FilterValueInItems = 'select-field.filter.value-in-items',
  MapSelectedItemOrUndefined = 'select-field.map.item_or_undefined',
  MapSelectedItem = 'select-field.map.item',
}

/**
 * Builder for field related graph nodes
 */
@Injectable({
  providedIn: 'root',
})
export class FieldGraphNodeBuilder extends AbstractGraphNodeBuilder<
  FieldNodeName,
  FieldOperatorName
> {
  constructor() {
    super();

    this.addNode(
      FieldNodeName.FieldProperty,
      (def: GraphInputDefinition<FormFieldProperty>, ctx) =>
        this.fieldProperty$(def.options ?? { property: 'key' }, ctx),
    );

    // Provide Operators
    this.addOperator(
      FieldOperatorName.FilterValueInItems,
      (def: OperatorNodeDefinition<[FieldReference]>, ctx) =>
        this.filterValueInItems(def, ctx),
    )
      .addOperator(
        FieldOperatorName.MapSelectedItemOrUndefined,
        (def: OperatorNodeDefinition<[FieldReference]>, ctx) =>
          this.mapToSelectedItemOrUndefined(def, ctx),
      )
      .addOperator(
        FieldOperatorName.MapSelectedItem,
        (def: OperatorNodeDefinition<[FieldReference]>, ctx) =>
          this.mapToSelectedItem(def, ctx),
      );
  }

  private fieldProperty$(
    { property, ref, isWidgetProperty = false }: FormFieldProperty,
    ctx: GraphBuilderContext,
  ) {
    const field = this.parseFieldReference(ref ?? { self: true }, ctx);

    return of(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      isWidgetProperty ? field.widgetOptions[property] : field[property],
    );
  }

  /**
   * Parses a JSON-like reference to a field.
   * If the referenced field can't be found or if the reference representation
   * is missing information, an error is thrown.
   *
   * @param ref A JSON-like representation of a field reference
   * @param fieldComponent The field component for which the reference is evaluated
   *
   * @returns
   * The referenced `CampaignField`
   */
  public parseFieldReference(
    ref: FieldReference,
    ctx: GraphBuilderContext,
  ): FieldDefinition {
    if (ref.self === true) {
      return this.getGenericField(ctx)?.field;
    }

    if (ref.path) {
      // Parse fieldset and field key
      const field = this.getDeclaration(ctx)?.get(ref.path);
      if (!field) {
        throw new Error(
          `Referenced field at path ${ref.path.join('.')} not found`,
        );
      }

      return field;
    }

    if (ref.fieldKey) {
      // Parse fieldset and field key
      const field = this.getDeclaration(ctx)?.findByKey(
        ref.fieldKey,
        isFieldDeclaration,
      );
      if (!field) {
        throw new Error(`Referenced field with key ${ref.fieldKey} not found`);
      }

      return field;
    }

    throw new Error('Invalid field reference');
  }

  public pathByFieldReference(
    ref: FieldReference,
    ctx: GraphBuilderContext,
  ): string[] {
    if (ref.self === true) {
      const genericComponent = this.getGenericField(ctx);
      return [genericComponent?.fieldset?.key, genericComponent?.field?.key];
    }

    if (ref.path) {
      // Parse fieldset and field key

      const field = this.getDeclaration(ctx)?.get(ref.path);
      if (!field) {
        throw new Error(
          `Referenced field with path ${ref.path.join('.')} not found`,
        );
      }

      return ref.path;
    }

    throw new Error('Invalid field reference');
  }

  /**
   * @param def Operator definition
   * @param ctx Context
   *
   * @returns
   * An operator that filters emission of values that are not
   * present in the fields `items`.
   */
  private filterValueInItems(
    def: OperatorNodeDefinition<[FieldReference]>,
    ctx: GraphBuilderContext,
  ) {
    const field = this.parseSelectFieldReference(
      def?.args?.[0] ?? { self: true },
      ctx,
    );

    return filterValueInSelectItems(field.widgetOptions.items);
  }

  /**
   * @param def Operator definition
   * @param ctx Context
   *
   * @returns
   * An operator that maps emitted values to
   */
  private mapToSelectedItemOrUndefined(
    def: OperatorNodeDefinition<[FieldReference]>,
    ctx: GraphBuilderContext,
  ): OperatorFunction<unknown, SelectInputItem | undefined> {
    const field = this.parseSelectFieldReference(
      def?.args?.[0] ?? { self: true },
      ctx,
    );

    return mapToSelectedItem(field.widgetOptions.items);
  }

  /**
   * @param def Operator definition
   * @param ctx Context
   *
   * @returns
   * An operator that emits with the selected item
   */
  public mapToSelectedItem(
    def: OperatorNodeDefinition<[FieldReference]>,
    ctx: GraphBuilderContext,
  ): OperatorFunction<unknown, SelectInputItem> {
    const operator = this.mapToSelectedItemOrUndefined(def, ctx);

    return (obs$) => obs$.pipe(operator, filter(isNotNullish));
  }

  private getGenericField(ctx: GraphBuilderContext): DeclarativeFieldComponent {
    return ctx.injector.get(DeclarativeFieldComponent);
  }

  private getDeclaration(ctx: GraphBuilderContext) {
    return this.getGenericField(ctx)?.declaration;
  }

  private parseSelectFieldReference(
    ref: FieldReference,
    ctx: GraphBuilderContext,
  ) {
    const field = this.parseFieldReference(ref, ctx);

    if (!isFormSelectField(field)) {
      throw new Error('Referenced field is not a CampaignSelectField');
    }

    return field;
  }
}
